import { z } from "zod";

// Response Schema
export const responseSchema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

// Member Attribute Choice Schema
export const memberAttributeChoiceSchema = z.object({
	attributeChoiceId: z.coerce.number().int(),
	memberAttributeId: z.coerce.number().int(),
	contents: z.coerce.string(),
	showOrder: z.coerce.number().int(),
});

// Member Attribute Type Enum
export const memberAttributeTypeSchema = z.enum([
	"text",
	"textarea",
	"number_integer",
	"number_float",
	"date",
	"time",
	"datetime",
	"boolean",
	"select",
	"radio",
	"checkbox",
	"file",
	"image",
	"video",
	"audio",
	"color",
	"url",
	"email",
	"telephone",
	"firstName",
	"lastName",
	"firstNameKana",
	"lastNameKana",
	"fullName",
	"fullNameKana",
	"address",
	"address_postal",
	"address_prefecture",
	"address_city",
	"address_address",
	"address_building",
]);

// Member Attribute Schema
export const memberAttributeSchema = z.object({
	memberAttributeId: z.coerce.number().int().optional(),
	required: z.coerce.boolean(),
	isMemberDisplayed: z.coerce.boolean().optional(),
	isAdminDisplayed: z.coerce.boolean().optional(),
	label: z.coerce.string(),
	type: memberAttributeTypeSchema,
	archType: z.coerce.string().nullable().optional(),
	section: z.coerce.string().nullable().optional(),
	showOrder: z.coerce.number().int(),
	attributeChoices: z.array(memberAttributeChoiceSchema).optional(),
});

// List Member Attributes API
export const listMemberAttributesQueryParamsSchema = z.object({}).optional();

/**
 * @description A list of member attributes
 */
export const listMemberAttributes200Schema = z.object({
	data: z.array(memberAttributeSchema),
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

/**
 * @description Bad request
 */
export const listMemberAttributes400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const listMemberAttributes500Schema = z.lazy(() => responseSchema);

export const listMemberAttributesQueryResponseSchema = z.lazy(
	() => listMemberAttributes200Schema,
);

// Create Member Attribute API
export const createMemberAttributeMutationRequestSchema = z.object({
	required: z.coerce.boolean(),
	isMemberDisplayed: z.coerce.boolean(),
	isAdminDisplayed: z.coerce.boolean(),
	label: z.coerce.string(),
	type: memberAttributeTypeSchema,
	choices: z
		.array(
			memberAttributeChoiceSchema.pick({
				contents: true,
				showOrder: true,
			}),
		)
		.optional(),
});

/**
 * @description Successfully created member attribute
 */
export const createMemberAttribute200Schema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

/**
 * @description Bad request
 */
export const createMemberAttribute400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const createMemberAttribute500Schema = z.lazy(() => responseSchema);

export const createMemberAttributeMutationResponseSchema = z.lazy(
	() => createMemberAttribute200Schema,
);

// Update Member Attribute API
export const updateMemberAttributePathParamsSchema = z.object({
	memberAttributeId: z.coerce
		.number()
		.int()
		.describe("ID of member attribute to update"),
});

export const updateMemberAttributeMutationRequestSchema = memberAttributeSchema
	.pick({
		required: true,
		label: true,
		isMemberDisplayed: true,
		isAdminDisplayed: true,
	})
	.extend({
		choices: z
			.array(
				memberAttributeChoiceSchema.pick({
					contents: true,
					showOrder: true,
				}),
			)
			.optional(),
	});

/**
 * @description Successfully updated member attribute
 */
export const updateMemberAttribute200Schema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

/**
 * @description Bad request
 */
export const updateMemberAttribute400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const updateMemberAttribute500Schema = z.lazy(() => responseSchema);

export const updateMemberAttributeMutationResponseSchema = z.lazy(
	() => updateMemberAttribute200Schema,
);

// Update Member Attribute Order API
export const updateMemberAttributeOrderMutationRequestSchema = z.object({
	memberAttributes: z.array(
		z.object({
			memberAttributeId: z.coerce.number().int(),
			showOrder: z.coerce.number().int(),
		}),
	),
});

/**
 * @description Successfully updated member attribute order
 */
export const updateMemberAttributeOrder200Schema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

/**
 * @description Bad request
 */
export const updateMemberAttributeOrder400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const updateMemberAttributeOrder500Schema = z.lazy(() => responseSchema);

export const updateMemberAttributeOrderMutationResponseSchema = z.lazy(
	() => updateMemberAttributeOrder200Schema,
);

// Delete Member Attribute API
export const deleteMemberAttributePathParamsSchema = z.object({
	memberAttributeId: z.coerce
		.number()
		.int()
		.describe("ID of member attribute to delete"),
});

/**
 * @description Successfully deleted member attribute
 */
export const deleteMemberAttribute200Schema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

/**
 * @description Bad request
 */
export const deleteMemberAttribute400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const deleteMemberAttribute500Schema = z.lazy(() => responseSchema);

export const deleteMemberAttributeMutationResponseSchema = z.lazy(
	() => deleteMemberAttribute200Schema,
);
